import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { KeycloakUser } from 'app/app.type';
import { environment } from 'environments/environment';
import { KeycloakService } from 'keycloak-angular';
import { Observable, catchError, from, of, switchMap, throwError, timeout } from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class KeycloakAdminService {
	private readonly httpClient = inject(HttpClient);
	private readonly keycloakService = inject(KeycloakService);

	private get keycloakAdminUrl(): string {
		return `${environment.auth.url}/admin/realms/${environment.auth.realm}`;
	}

	/**
	 * Fetches all users from Keycloak
	 */
	getAllUsers(): Observable<KeycloakUser[]> {
		return from(this.keycloakService.getToken()).pipe(
			switchMap(token => {
				const headers = new HttpHeaders({
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				});

				return this.httpClient.get<KeycloakUser[]>(
					`${this.keycloakAdminUrl}/users`,
					{ headers }
				);
			}),
			catchError((error: HttpErrorResponse) => {
				console.error('Error fetching users from Keycloak:', error);

				if (error.status === 403) {
					return throwError(() => new Error('User does not have administrative permissions'));
				} else if (error.status === 401) {
					return throwError(() => new Error('Invalid or expired authentication token'));
				} else {
					return throwError(() => new Error('Error connecting to user service'));
				}
			})
		);
	}

	/**
	 * Fetches user by ID
	 */
	getUserById(userId: string): Observable<KeycloakUser> {
		return from(this.keycloakService.getToken()).pipe(
			switchMap(token => {
				const headers = new HttpHeaders({
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				});

				return this.httpClient.get<KeycloakUser>(
					`${this.keycloakAdminUrl}/users/${userId}`,
					{ headers }
				);
			}),
			catchError((error: HttpErrorResponse) => {
				console.error('Error fetching user from Keycloak:', error);

				if (error.status === 403) {
					return throwError(() => new Error('User does not have administrative permissions.'));
				} else if (error.status === 401) {
					return throwError(() => new Error('Invalid or expired authentication session.'));
				} else if (error.status === 404) {
					return throwError(() => new Error('User not found.'));
				} else {
					return throwError(() => new Error('Error connecting to user service.'));
				}
			})
		);
	}

	/**
	 * Checks if the current user has administrative permissions
	 */
	hasAdminPermissions(): Observable<boolean> {
		return from(this.keycloakService.getToken()).pipe(
			timeout(10000), // 10 second timeout to avoid infinite loading
			switchMap(token => {
				const headers = new HttpHeaders({
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				});

				// Tries to make a simple request to check permissions
				return this.httpClient.get<any>(
					`${this.keycloakAdminUrl}/users?max=1`,
					{ headers }
				);
			}),
			switchMap(() => {
				return of(true);
			}),
			catchError((error) => {
				console.error('Error checking admin permissions:', error);
				return of(false);
			})
		);
	}

	generateTemporaryPassword(): string {
		return String(Math.random().toString(36).slice(-8));
	}

	/**
	 * Creates a new user in Keycloak
	 */
	createUser(userData: Partial<KeycloakUser>, password: string): Observable<any> {
		return from(this.keycloakService.getToken()).pipe(
			switchMap(token => {
				const headers = new HttpHeaders({
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				});

				const userPayload = {
					...userData,
					credentials: [{
						type: 'password',
						value: password,
						temporary: true,

					}]
				};

				return this.httpClient.post(
					`${this.keycloakAdminUrl}/users`,
					userPayload,
					{ headers }
				);
			}),
			catchError((error: HttpErrorResponse) => {
				console.error('Error creating user in Keycloak:', error);

				if (error.status === 403) {
					return throwError(() => new Error('User does not have administrative permissions.'));
				} else if (error.status === 401) {
					return throwError(() => new Error('Invalid or expired authentication session.'));
				} else if (error.status === 409) {
					return throwError(() => new Error('User already exists.'));
				} else {
					return throwError(() => new Error('Error connecting to user service.'));
				}
			})
		);
	}

	/**
	 * Updates a user in Keycloak
	 */
	updateUser(userId: string, userData: Partial<KeycloakUser>): Observable<any> {
		return from(this.keycloakService.getToken()).pipe(
			switchMap(token => {
				const headers = new HttpHeaders({
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				});

				return this.httpClient.put(
					`${this.keycloakAdminUrl}/users/${userId}`,
					userData,
					{ headers }
				);
			}),
			catchError((error: HttpErrorResponse) => {
				console.error('Error updating user in Keycloak:', error);

				if (error.status === 403) {
					return throwError(() => new Error('User does not have administrative permissions.'));
				} else if (error.status === 401) {
					return throwError(() => new Error('Invalid or expired authentication session.'));
				} else if (error.status === 404) {
					return throwError(() => new Error('User not found.'));
				} else {
					return throwError(() => new Error('Error connecting to user service.'));
				}
			})
		);
	}

	/**
	 * Resets a user's password in Keycloak
	 */
	resetUserPassword(userId: string, newPassword: string, temporary: boolean = true): Observable<any> {
		return from(this.keycloakService.getToken()).pipe(
			switchMap(token => {
				const headers = new HttpHeaders({
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				});

				const credentialPayload = {
					type: 'password',
					value: newPassword,
					temporary: temporary
				};

				return this.httpClient.put(
					`${this.keycloakAdminUrl}/users/${userId}/reset-password`,
					credentialPayload,
					{ headers }
				);
			}),
			catchError((error: HttpErrorResponse) => {
				console.error('Error resetting user password in Keycloak:', error);

				if (error.status === 403) {
					return throwError(() => new Error('User does not have administrative permissions.'));
				} else if (error.status === 401) {
					return throwError(() => new Error('Invalid or expired authentication session.'));
				} else if (error.status === 404) {
					return throwError(() => new Error('User not found.'));
				} else {
					return throwError(() => new Error('Error connecting to user service.'));
				}
			})
		);
	}

	manageUserGroup(userId: string, groupId: string, method: 'put' | 'delete'): Observable<any> {
		return from(this.keycloakService.getToken()).pipe(
			switchMap(token => {
				const headers = new HttpHeaders({
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				});

				if (method === 'delete') {
					return this.httpClient.delete(
						`${this.keycloakAdminUrl}/users/${userId}/groups/${groupId}`,
						{ headers }
					);
				}

				return this.httpClient.put(
					`${this.keycloakAdminUrl}/users/${userId}/groups/${groupId}`,
					{ headers }
				);
			}),
			catchError((error: HttpErrorResponse) => {
				console.error('Error updating user groups in Keycloak:', error);
				if (error.status === 403) {
					return throwError(() => new Error('User does not have administrative permissions.'));
				} else if (error.status === 401) {
					return throwError(() => new Error('Invalid or expired authentication session.'));
				} else if (error.status === 404) {
					return throwError(() => new Error('User not found.'));
				} else {
					return throwError(() => new Error('Error connecting to user service.'));
				}
			})
		);
	}

	/**
	 * Gets role object by name from available realm roles
	 */
	private getRoleByName(roleName: string): Observable<any> {
		return from(this.keycloakService.getToken()).pipe(
			switchMap(token => {
				const headers = new HttpHeaders({
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				});

				return this.httpClient.get<any>(
					`${this.keycloakAdminUrl}/roles/${roleName}`,
					{ headers }
				);
			}),
			catchError((error: HttpErrorResponse) => {
				console.error('❌ Role search failed:', roleName, error);
				return throwError(() => error);
			})
		);
	}

	manageUserRole(userId: string, roleName: string, method: 'put' | 'delete'): Observable<any> {

		// First, get the complete role object from Keycloak
		return this.getRoleByName(roleName).pipe(
			switchMap(roleObject => {

				return from(this.keycloakService.getToken()).pipe(
					switchMap(token => {
						const headers = new HttpHeaders({
							'Authorization': `Bearer ${token}`,
							'Content-Type': 'application/json'
						});

						// Use the complete role object with id and name
						const roleRepresentation = [roleObject];
						const endpoint = `${this.keycloakAdminUrl}/users/${userId}/role-mappings/realm`;

						if (method === 'delete') {
							return this.httpClient.delete(
								endpoint,
								{
									headers,
									body: roleRepresentation
								}
							);
						}

						return this.httpClient.post(
							endpoint,
							roleRepresentation,
							{ headers }
						);
					})
				);
			}),
			catchError((error: HttpErrorResponse) => {
				console.error('❌ Error managing user role:', error);
				if (error.status === 403) {
					return throwError(() => new Error('User does not have administrative permissions.'));
				} else if (error.status === 401) {
					return throwError(() => new Error('Invalid or expired authentication session.'));
				} else if (error.status === 404) {
					const message = error.url?.includes('/roles/') ? 'Role not found.' : 'User not found.';
					return throwError(() => new Error(message));
				} else {
					return throwError(() => new Error('Error connecting to user service.'));
				}
			})
		);
	}

	getGroups(): Observable<any[]> {
		return from(this.keycloakService.getToken()).pipe(
			switchMap(token => {
				const headers = new HttpHeaders({
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				});
				return this.httpClient.get<any[]>(
					`${this.keycloakAdminUrl}/groups`,
					{ headers }
				);
			})
		);
	}

	/**
	 * Fetches users from Keycloak with pagination
	 */
	getUsersWithPagination(first: number = 0, max: number = 10): Observable<KeycloakUser[]> {
		return from(this.keycloakService.getToken()).pipe(
			switchMap(token => {
				const headers = new HttpHeaders({
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				});

				const params = new URLSearchParams({
					first: first.toString(),
					max: max.toString()
				});

				return this.httpClient.get<KeycloakUser[]>(
					`${this.keycloakAdminUrl}/users?${params.toString()}`,
					{ headers }
				);
			}),
			catchError((error: HttpErrorResponse) => {
				console.error('Error fetching users from Keycloak:', error);

				if (error.status === 403) {
					return throwError(() => new Error('User does not have administrative permissions'));
				} else if (error.status === 401) {
					return throwError(() => new Error('Invalid or expired authentication token'));
				} else {
					return throwError(() => new Error('Error connecting to user service'));
				}
			})
		);
	}

	/**
	 * Gets the total count of users from Keycloak
	 */
	getUsersCount(): Observable<number> {
		return from(this.keycloakService.getToken()).pipe(
			switchMap(token => {
				const headers = new HttpHeaders({
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				});

				return this.httpClient.get<number>(
					`${this.keycloakAdminUrl}/users/count`,
					{ headers }
				);
			}),
			catchError((error: HttpErrorResponse) => {
				console.error('Error fetching users count from Keycloak:', error);

				if (error.status === 403) {
					return throwError(() => new Error('User does not have administrative permissions'));
				} else if (error.status === 401) {
					return throwError(() => new Error('Invalid or expired authentication token'));
				} else {
					return throwError(() => new Error('Error connecting to user service'));
				}
			})
		);
	}

	getUserGroups(userId: string): Observable<any[]> {
		return from(this.keycloakService.getToken()).pipe(
			switchMap(token => {
				const headers = new HttpHeaders({
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				});
				return this.httpClient.get<any[]>(
					`${this.keycloakAdminUrl}/users/${userId}/groups`,
					{ headers }
				);
			})
		);

	}

	getUserRoles(userId: string): Observable<any[]> {
		return from(this.keycloakService.getToken()).pipe(
			switchMap(token => {
				const headers = new HttpHeaders({
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				});
				return this.httpClient.get<any[]>(
					`${this.keycloakAdminUrl}/users/${userId}/role-mappings/realm/composite`,
					{ headers }
				);
			})
		);
	}

	getAvaliableRoles(): Observable<any[]> {
		return from(this.keycloakService.getToken()).pipe(
			switchMap(token => {
				const headers = new HttpHeaders({
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				});
				return this.httpClient.get<any[]>(
					`${this.keycloakAdminUrl}/roles`,
					{ headers }
				);
			})
		);
	}

	/**
	 * Gets roles that are available to be assigned to a specific user
	 * (roles that the user doesn't already have)
	 */
	getAvailableRolesForUser(userId: string): Observable<any[]> {
		return from(this.keycloakService.getToken()).pipe(
			switchMap(token => {
				const headers = new HttpHeaders({
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				});
				return this.httpClient.get<any[]>(
					`${this.keycloakAdminUrl}/users/${userId}/role-mappings/realm/available`,
					{ headers }
				);
			})
		);
	}

	/**
	 * Gets members of a specific group
	 */
	getGroupMembers(groupId: string): Observable<KeycloakUser[]> {
		return from(this.keycloakService.getToken()).pipe(
			switchMap(token => {
				const headers = new HttpHeaders({
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				});
				return this.httpClient.get<KeycloakUser[]>(
					`${this.keycloakAdminUrl}/groups/${groupId}/members`,
					{ headers }
				);
			})
		);
	}
}
